import EnqueryForm from "@/components/forms/EnqueryForm";
import { FormHeading, SuccessMessageType } from "@/interface/types";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface PopupFormProps {
  handleClose: () => void;
  pageName: string;
}

const PopupForm: React.FC<PopupFormProps> = ({ handleClose, pageName }) => {
  const [isSuccess, setIsSuccess] = useState(false);
  // Portalled to <body> so the overlay escapes ancestor stacking contexts —
  // e.g. the home banner section uses `contain: layout`, which would otherwise
  // trap this `fixed` overlay inside the banner's box and paint it under the
  // header. Mount-gated because document isn't available during SSR.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const courseId =
    pageName === "CPA"
      ? 1
      : pageName === "CPA/CMA"
        ? 1
        : pageName === "CMA"
          ? 2
          : pageName === "EA"
            ? 19
            : pageName === "CAIRA"
              ? 18
              : 1;

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[10000] p-4">
      <div
        className="md:hidden relative w-full max-w-[400px] rounded-2xl overflow-hidden bg-[#0d0d0d]"
        style={{ boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)" }}
      >
        <button
          onClick={handleClose}
          aria-label="Close"
          className="text-white text-base absolute right-3 top-3 cursor-pointer w-5 h-5 flex items-center justify-center z-30"
        >
          ✕
        </button>
        <div className="p-5 pt-6">
          <EnqueryForm
            courseId={courseId}
            pageName={pageName}
            darkBackground={true}
            submitButtonName="Register For Free Webinar"
            formType="page"
            isAStepperForm
            formHeading={FormHeading.MONTHLY_WEBINAR}
            successMessage={SuccessMessageType.ACCOUNTING}
          />
        </div>
      </div>

      {/* Desktop: white popup with banner image */}
      <div
        className={`hidden md:flex relative w-full rounded-2xl overflow-hidden bg-white md:flex-row ${isSuccess ? "max-w-[400px] " : "max-w-[615px] "}`}
        style={{ boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)" }}
      >
        <div
          onClick={handleClose}
          aria-label="Close"
          className="text-[#3399ff] absolute right-3 top-3 cursor-pointer w-8 h-8 p-2 flex items-center justify-center rounded-full border border-[#3399ff] bg-white z-30"
        >
          ✕
        </div>

        {/* Left: Banner image (hidden once the success message is shown) */}
        {!isSuccess && (
          <div className="hidden md:block md:w-[50%] relative flex-shrink-0">
            <Image
              src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/pop_upBanner.webp"
              alt="CPA Webinar Banner"
              fill
              className=" object-center"
              loading="lazy"
            />
          </div>
        )}

        {/* Right: Form */}
        <div
          className={`relative z-10 p-3 w-full ${isSuccess ? "md:w-full" : "md:w-[50%]"}`}
        >
          <EnqueryForm
            courseId={courseId}
            pageName={pageName}
            darkBackground={false}
            submitButtonName="Register for Webinar"
            formType="page"
            isAStepperForm
            formHeading={FormHeading.POP_UP_FORM}
            successMessage={SuccessMessageType.ACCOUNTING}
            onSuccessStateChange={setIsSuccess}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PopupForm;
